/**
 * PlacementPanel - Configuration panel for bulk rack placement
 * Allows users to configure linear and mirror placement patterns
 */

import React from 'react';
import {
  Rows3,
  FlipHorizontal,
  ArrowRight,
  ArrowDown,
  Ruler,
  Check,
  X,
  Anchor,
  AlertCircle,
  Info
} from 'lucide-react';
import {
  PlacementConfig,
  PlacementMode,
  PlacementDirection,
  PreviewRackPosition,
  RackSummary,
  DEFAULT_PLACEMENT_CONFIG
} from '../types/warehouseLayout';

interface PlacementPanelProps {
  config: PlacementConfig;
  onConfigChange: (config: PlacementConfig) => void;
  selectedUnplacedRacks: string[];
  anchorRack: string | null;
  previewPositions: PreviewRackPosition[];
  onApply: () => void;
  onCancel: () => void;
  onSetAnchor: () => void;
  unplacedRacks: RackSummary[];
  isVisible: boolean;
}

const PlacementPanel: React.FC<PlacementPanelProps> = ({
  config,
  onConfigChange,
  selectedUnplacedRacks,
  anchorRack,
  previewPositions,
  onApply,
  onCancel,
  onSetAnchor,
  unplacedRacks,
  isVisible
}) => {
  if (!isVisible) return null;

  const validPositions = previewPositions.filter(p => p.isValid);
  const invalidPositions = previewPositions.filter(p => !p.isValid);
  const canApply = validPositions.length > 0 && anchorRack;

  const handleModeChange = (mode: PlacementMode) => {
    onConfigChange({ ...config, mode });
  };

  const handleDirectionChange = (direction: PlacementDirection) => {
    onConfigChange({ ...config, direction });
  };

  const handleValueChange = (field: keyof PlacementConfig, value: number) => {
    onConfigChange({ ...config, [field]: value });
  };

  const resetToDefaults = () => {
    onConfigChange(DEFAULT_PLACEMENT_CONFIG);
  };

  return (
    <div className="bg-white border-l border-gray-200 w-72 flex flex-col h-full flex-shrink-0">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-blue-50">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Ruler className="w-5 h-5 text-blue-600" />
          Bulk-plassering
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          Plasser flere reoler automatisk
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Step 1: Select anchor */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="bg-blue-600 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">1</span>
            <span className="font-medium text-sm">Velg ankerreol</span>
          </div>
          <div className="ml-7">
            {anchorRack ? (
              <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                <Anchor className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-800">Reol {anchorRack}</span>
              </div>
            ) : (
              <div className="p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  Klikk på en plassert reol i 2D-visningen for å sette den som anker
                </p>
                <button
                  onClick={onSetAnchor}
                  className="mt-2 text-xs px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Velg anker
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Step 2: Placement mode */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="bg-blue-600 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">2</span>
            <span className="font-medium text-sm">Velg plasseringsmodus</span>
          </div>
          <div className="ml-7 grid grid-cols-2 gap-2">
            <button
              onClick={() => handleModeChange('linear')}
              className={`flex flex-col items-center p-3 rounded-lg border-2 transition-colors ${
                config.mode === 'linear'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <Rows3 className={`w-6 h-6 mb-1 ${config.mode === 'linear' ? 'text-blue-600' : 'text-gray-500'}`} />
              <span className={`text-xs font-medium ${config.mode === 'linear' ? 'text-blue-700' : 'text-gray-600'}`}>
                Enkel rad
              </span>
            </button>
            <button
              onClick={() => handleModeChange('mirror')}
              className={`flex flex-col items-center p-3 rounded-lg border-2 transition-colors ${
                config.mode === 'mirror'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <FlipHorizontal className={`w-6 h-6 mb-1 ${config.mode === 'mirror' ? 'text-blue-600' : 'text-gray-500'}`} />
              <span className={`text-xs font-medium ${config.mode === 'mirror' ? 'text-blue-700' : 'text-gray-600'}`}>
                Dobbel rad
              </span>
            </button>
          </div>
        </div>

        {/* Direction toggle */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="bg-blue-600 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">3</span>
            <span className="font-medium text-sm">Retning</span>
          </div>
          <div className="ml-7 grid grid-cols-2 gap-2">
            <button
              onClick={() => handleDirectionChange('horizontal')}
              className={`flex items-center justify-center gap-2 p-2 rounded-lg border-2 transition-colors ${
                config.direction === 'horizontal'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <ArrowRight className={`w-4 h-4 ${config.direction === 'horizontal' ? 'text-blue-600' : 'text-gray-500'}`} />
              <span className={`text-xs ${config.direction === 'horizontal' ? 'text-blue-700' : 'text-gray-600'}`}>
                Horisontalt
              </span>
            </button>
            <button
              onClick={() => handleDirectionChange('vertical')}
              className={`flex items-center justify-center gap-2 p-2 rounded-lg border-2 transition-colors ${
                config.direction === 'vertical'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <ArrowDown className={`w-4 h-4 ${config.direction === 'vertical' ? 'text-blue-600' : 'text-gray-500'}`} />
              <span className={`text-xs ${config.direction === 'vertical' ? 'text-blue-700' : 'text-gray-600'}`}>
                Vertikalt
              </span>
            </button>
          </div>
        </div>

        {/* Configuration values */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="bg-blue-600 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">4</span>
            <span className="font-medium text-sm">Avstander (meter)</span>
          </div>
          <div className="ml-7 space-y-3">
            {/* Rack spacing */}
            <div>
              <label className="flex items-center justify-between text-xs text-gray-600 mb-1">
                <span>Mellomrom i rad</span>
                <span className="font-mono">{config.rackSpacing.toFixed(1)}m</span>
              </label>
              <input
                type="range"
                min="0.2"
                max="2.0"
                step="0.1"
                value={config.rackSpacing}
                onChange={(e) => handleValueChange('rackSpacing', parseFloat(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>

            {/* Aisle width (only for mirror mode) */}
            {config.mode === 'mirror' && (
              <div>
                <label className="flex items-center justify-between text-xs text-gray-600 mb-1">
                  <span>Gangbredde</span>
                  <span className="font-mono">{config.aisleWidth.toFixed(1)}m</span>
                </label>
                <input
                  type="range"
                  min="1.5"
                  max="4.0"
                  step="0.1"
                  value={config.aisleWidth}
                  onChange={(e) => handleValueChange('aisleWidth', parseFloat(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>Smal (1.5m)</span>
                  <span>Bred (4.0m)</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Selection summary */}
        <div className="bg-gray-50 rounded-lg p-3">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Oppsummering</h4>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Valgte reoler:</span>
              <span className="font-medium">{selectedUnplacedRacks.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Gyldige plasseringer:</span>
              <span className="font-medium text-green-600">{validPositions.length}</span>
            </div>
            {invalidPositions.length > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-500">Ugyldige (kollisjon):</span>
                <span className="font-medium text-red-600">{invalidPositions.length}</span>
              </div>
            )}
          </div>
        </div>

        {/* Warnings */}
        {invalidPositions.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-amber-800">Noen reoler kolliderer</p>
                <p className="text-amber-700 mt-1">
                  {invalidPositions.length} reol{invalidPositions.length > 1 ? 'er' : ''} kan ikke plasseres.
                  Prøv å justere avstander eller velg en annen ankerposisjon.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Info about preview */}
        {previewPositions.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-blue-700">
                Se forhåndsvisning i 2D-visningen. Grønne reoler er gyldige, røde kolliderer.
              </p>
            </div>
          </div>
        )}

        {/* Reset button */}
        <button
          onClick={resetToDefaults}
          className="text-sm text-gray-500 hover:text-gray-700 underline"
        >
          Tilbakestill til standardverdier
        </button>
      </div>

      {/* Footer actions */}
      <div className="p-4 border-t border-gray-200 bg-gray-50 space-y-2">
        <button
          onClick={onApply}
          disabled={!canApply}
          className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
            canApply
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          <Check className="w-4 h-4" />
          Plasser {validPositions.length} reoler
        </button>
        <button
          onClick={onCancel}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
        >
          <X className="w-4 h-4" />
          Avbryt
        </button>
      </div>
    </div>
  );
};

export default PlacementPanel;
